/**
 * Centè custom cart — drawer + cart page, one renderer.
 *
 * WHY THIS EXISTS
 *   The Summer Set reward logic was originally layered on top of Horizon's cart.
 *   Horizon re-renders the cart section on every mutation (Section Rendering API
 *   + morph), which REPLACES the DOM and does not re-execute inline scripts. Any
 *   code layered on top ends up writing to a detached node, and its server-rendered
 *   Liquid races whatever the JS just painted. That produced: a bar that updated
 *   once then froze, gifts that needed a page refresh to appear, and a checkout
 *   gate that intermittently failed open.
 *
 *   This cart owns its own rendering end to end. One source of truth (/cart.js),
 *   one render function, no section rendering, no morph, nothing to race.
 *
 * DELIBERATELY A CLASSIC SCRIPT
 *   No ES module, no importmap, no bare specifiers, no custom-element upgrade.
 *   Every one of those was a way the previous implementation silently died.
 *
 * HARD RULES (agency bug ledger)
 *   B-011  every mutation is keyed on line.key, never an index. Terrain Shorts can
 *          legitimately appear twice — once paid, once gifted.
 *   B-016  all merchant/customer text is inserted with textContent, never innerHTML.
 *   B-024  a mutation counts as failed only on a real error response.
 *   B-031  no dynamic import().
 */
(function () {
  'use strict';

  var CFG = null;
  var cart = null;
  var busy = false;
  var chain = Promise.resolve();
  var sizesConfirmed = false;
  var lastFocus = null;

  var GIFT_FLAG = 'summer-set';
  var SLOTS = [
    { key: 'terrain', slot: 'terrain', label: 'Free Terrain Shorts', colour: true },
    { key: 'tee1', slot: 'tee-1', label: 'Free Mystery Archive Tee', colour: false },
    { key: 'tee2', slot: 'tee-2', label: 'Second free Mystery Archive Tee', colour: false }
  ];

  /* ------------------------------------------------------------------ *
   * helpers
   * ------------------------------------------------------------------ */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function route(name, fallback) {
    var r = (window.Theme && window.Theme.routes) || {};
    return r[name] || fallback;
  }

  function money(amount) {
    var code = (cart && cart.currency) || (CFG && CFG.currency) || 'GBP';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code })
        .format((Number(amount) || 0) / 100);
    } catch (e) {
      return ((Number(amount) || 0) / 100).toFixed(2);
    }
  }

  /** Serialise cart writes — concurrent /cart/* calls return stale carts and 422s. */
  function queue(task) {
    chain = chain.then(task).catch(function (err) { console.error('[cente-cart]', err); });
    return chain;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.text().then(function (text) {
        var data;
        try { data = text ? JSON.parse(text) : {}; } catch (e) { data = { raw: text }; }
        // B-024: only a real error payload or non-ok status is a failure.
        if (!res.ok || data.status === 422 || data.errors) {
          var reason = data.description || data.message ||
            (data.errors && (typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors))) ||
            ('HTTP ' + res.status);
          var err = new Error(reason);
          // Carry the full context so a failure is diagnosable rather than generic.
          err.status = res.status;
          err.payload = data;
          err.request = { url: url, body: body };
          throw err;
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* ------------------------------------------------------------------ *
   * reward rules — delegated to assets/cente-reward-rules.js
   *
   * That file is pure, has no DOM or network, and is unit-tested in
   * _test_cente_reward.mjs. Keeping the rules there rather than inline means the
   * money-critical behaviour (strip every gift the moment the cart drops below the
   * threshold) is covered by tests instead of asserted.
   * ------------------------------------------------------------------ */

  function R() { return window.CenteReward; }

  function rate() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1';
  }

  function isGift(line) { return R().isGift(line); }
  function slotOf(line) { return R().slotOf(line); }
  function giftLines() { return R().giftLines(cart); }
  function paidLines() { return R().paidLines(cart); }
  function threshold() { return R().threshold(CFG, rate()); }
  function eligibleSubtotal() { return R().eligibleSubtotal(cart, CFG.eligible); }
  function promisedSlots() { return R().promisedSlots(CFG); }
  function remaining() { return R().remaining(cart, CFG, rate()); }
  function qualifies() { return R().qualifies(cart, CFG, rate()); }
  function giftsComplete() { return R().giftsComplete(cart, CFG); }
  function giftValue() { return R().giftValue(CFG); }
  function stocked(entry) { return R().stocked(entry); }
  function pickVariant(entry, opts) { return R().pickVariant(entry, opts); }

  /**
   * Bring gift lines in line with qualification.
   *
   * Removal is unconditional and comes FIRST: the instant the eligible subtotal
   * drops below the threshold, every gift line goes. It never depends on the modal,
   * on the shopper having chosen anything, or on which control shrank the cart.
   *
   * Adds are all-or-nothing. Shopify's BXGY needs the full "customer gets" quantity
   * present before it applies, so a partial set means the shopper is charged.
   */
  function reconcile() {
    // Safe to run off whatever cart is current: every write below is keyed on a
    // line.key taken from that same object, and the guard at the end re-reads from
    // the server before deciding whether anything survived.
    var toRemove = R().giftsToRemove(cart, CFG, rate());
    var toFix = R().giftsToFix(cart, CFG, rate());
    var toAdd = R().giftsToAdd(cart, CFG, rate());
    if (!toRemove.length && !toFix.length && !toAdd.length) return Promise.resolve(false);

    var work = Promise.resolve();

    // B-011: keyed on line.key. Terrain can appear twice — once paid, once gifted.
    function step(body) {
      return function () {
        // A blank id is dropped by JSON.stringify, so the request would arrive
        // without one and Shopify answers "no valid id or line parameter".
        if (typeof body.id !== 'string' || !body.id) {
          console.error('[cente-cart] refusing cart write with no line key', body);
          return Promise.resolve();
        }
        return post(route('cart_change_url', '/cart/change') + '.js', body)
          .then(function (u) { cart = u; })
          .catch(function (err) {
            // Isolated: a single failed line must not abandon the remaining
            // removals, nor the verification guard at the end.
            console.error('[cente-cart] line write failed', body, err);
          });
      };
    }

    toRemove.forEach(function (key) { work = work.then(step({ id: key, quantity: 0 })); });
    toFix.forEach(function (fix) { work = work.then(step({ id: fix.key, quantity: fix.quantity })); });
    if (toAdd.length) {
      work = work.then(function () {
        return post(route('cart_add_url', '/cart/add') + '.js', { items: toAdd })
          .then(function (u) { cart = u; });
      });
    }

    return work
      .then(function () {
        return post(route('cart_update_url', '/cart/update') + '.js', {
          attributes: { summer_set_active: qualifies() ? 'true' : '' }
        });
      })
      .then(function (u) { cart = u; })
      .then(function () {
        // Never leave free product in a cart that no longer qualifies. Re-read from
        // the server (our local copy may be stale after a failed write) and retry
        // once before giving up loudly.
        return getCart().then(function (fresh) {
          cart = fresh;
          var stillWrong = R().giftsToRemove(cart, CFG, rate());
          if (!stillWrong.length) return true;

          console.warn('[cente-cart] gifts survived reconcile, retrying', stillWrong);
          var retry = Promise.resolve();
          stillWrong.forEach(function (key) { retry = retry.then(step({ id: key, quantity: 0 })); });
          return retry
            .then(getCart)
            .then(function (f) {
              cart = f;
              var remainingWrong = R().giftsToRemove(cart, CFG, rate());
              if (remainingWrong.length) {
                console.error('[cente-cart] COULD NOT REMOVE GIFT LINES', remainingWrong,
                  (cart.items || []).filter(R().isGift));
              }
              return true;
            });
        });
      });
  }

  /* ------------------------------------------------------------------ *
   * rendering
   * ------------------------------------------------------------------ */

  function lineTemplate() {
    var tpl = $('#cente-cart-line-template');
    return tpl ? tpl.content.firstElementChild.cloneNode(true) : null;
  }

  function renderLines(root) {
    var list = $('[data-cente-cart-lines]', root);
    if (!list) return;

    // Index the nodes already on screen so they can be reused rather than rebuilt.
    // Wiping the list on every render caused visible flicker, lost scroll position
    // and made the browser re-decode every image.
    var existing = {};
    $$('[data-key]', list).forEach(function (n) {
      var k = n.getAttribute('data-key');
      if (k) existing[k] = n;
    });
    var seen = {};

    (cart.items || []).forEach(function (line) {
      var node = existing[line.key] || lineTemplate();
      if (!node) return;
      seen[line.key] = true;
      var gift = isGift(line);

      node.setAttribute('data-key', line.key);
      if (gift) node.setAttribute('data-gift', '');

      var img = $('[data-cente-line-image]', node);
      if (img) {
        if (line.image) {
          // Shopify CDN sizing param; line.image is a CDN URL. Only reassign when
          // it actually changes, or the browser re-decodes the image each render.
          var src = line.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
          if (img.getAttribute('src') !== src) { img.src = src; img.alt = ''; }
        } else {
          img.remove();
        }
      }

      // B-016: merchant/customer text via textContent, never innerHTML.
      var title = $('[data-cente-line-title]', node);
      if (title) title.textContent = line.product_title || line.title || '';

      var variant = $('[data-cente-line-variant]', node);
      if (variant) {
        var bits = (line.options_with_values || [])
          .filter(function (o) { return o.value && String(o.value).toLowerCase() !== 'default title'; })
          .map(function (o) { return o.value; });
        variant.textContent = bits.join(' · ');
        variant.hidden = bits.length === 0;
      }

      var price = $('[data-cente-line-price]', node);
      if (price) {
        if (gift) {
          price.textContent = 'FREE';
          price.setAttribute('data-free', '');
        } else {
          price.textContent = money(line.final_line_price != null ? line.final_line_price : line.line_price);
        }
      }

      var was = $('[data-cente-line-was]', node);
      if (was) {
        // Gifts show their retail value struck through; paid lines show a
        // strike only when a discount actually reduced them.
        var original = Number(line.original_line_price) || 0;
        var current = Number(line.final_line_price != null ? line.final_line_price : line.line_price) || 0;
        if (gift) {
          was.textContent = money(original || current);
          was.hidden = false;
        } else if (original > current) {
          was.textContent = money(original);
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }

      var badge = $('[data-cente-line-badge]', node);
      if (badge) badge.hidden = !gift;

      // Gift lines are not customer-editable: no stepper, no remove.
      var controls = $('[data-cente-line-controls]', node);
      if (controls) controls.hidden = gift;

      var qty = $('[data-cente-line-qty]', node);
      if (qty) qty.textContent = String(line.quantity);

      var discounts = $('[data-cente-line-discounts]', node);
      if (discounts) {
        discounts.textContent = '';
        (line.line_level_discount_allocations || []).forEach(function (d) {
          var li = document.createElement('li');
          li.textContent = d.discount_application ? d.discount_application.title : (d.title || '');
          discounts.appendChild(li);
        });
        discounts.hidden = !(line.line_level_discount_allocations || []).length;
      }

      var unit = $('[data-cente-line-unit]', node);
      if (unit) {
        if (line.unit_price_measurement && line.unit_price) {
          var m = line.unit_price_measurement;
          unit.textContent = money(line.unit_price) + ' / ' +
            (m.reference_value !== 1 ? m.reference_value : '') + m.reference_unit;
          unit.hidden = false;
        } else {
          unit.hidden = true;
        }
      }

      // appendChild on an already-present node moves it, keeping DOM order in sync
      // with cart order without detaching anything.
      list.appendChild(node);
    });

    // Drop rows whose line is gone.
    Object.keys(existing).forEach(function (k) {
      if (!seen[k] && existing[k].parentNode) existing[k].parentNode.removeChild(existing[k]);
    });
  }

  function renderTotals(root) {
    var count = (cart.items || []).reduce(function (n, l) { return n + (Number(l.quantity) || 0); }, 0);

    $$('[data-cente-cart-count]').forEach(function (n) { n.textContent = String(count); });

    /* Totals shown to the shopper EXCLUDE the free gifts.
     *
     * Shopify's cart.total_price includes gift lines until its discount applies.
     * Subtracting each gift's CURRENT price is self-correcting: once the BXGY does
     * apply, a gift's final_line_price is already 0 and this subtracts nothing.
     * Until then the shopper sees what the offer promises instead of a total that
     * contradicts the FREE labels on the lines.
     *
     * Caveat: this makes the displayed total depend on the discount being correctly
     * configured. If it never fires, checkout will be higher than the cart showed. */
    var giftAmount = giftLines().reduce(function (sum, l) {
      var p = l.final_line_price != null ? l.final_line_price : l.line_price;
      return sum + (Number(p) || 0);
    }, 0);

    var paidSubtotal = paidLines().reduce(function (sum, l) {
      var p = l.final_line_price != null ? l.final_line_price : l.line_price;
      return sum + (Number(p) || 0);
    }, 0);

    var displayTotal = Math.max(0, (Number(cart.total_price) || 0) - giftAmount);

    var sub = $('[data-cente-cart-subtotal]', root);
    if (sub) sub.textContent = money(paidSubtotal);

    var tot = $('[data-cente-cart-total]', root);
    if (tot) tot.textContent = money(displayTotal);

    // What the gifts are worth, framed as the saving rather than a line total.
    var savings = $('[data-cente-cart-savings]', root);
    if (savings) {
      var giftWorth = giftLines().reduce(function (sum, l) {
        return sum + (Number(l.original_line_price) || 0);
      }, 0);
      var discounted = (Number(cart.items_subtotal_price) || 0) - (Number(cart.total_price) || 0);
      var saved = giftWorth + Math.max(0, discounted);
      if (saved > 0) { savings.textContent = 'You save ' + money(saved); savings.hidden = false; }
      else savings.hidden = true;
    }

    var discounts = $('[data-cente-cart-discounts]', root);
    if (discounts) {
      discounts.textContent = '';
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        var li = document.createElement('li');
        li.textContent = d.title + ' (\u2212' + money(d.total_allocated_amount) + ')';
        discounts.appendChild(li);
      });
      discounts.hidden = !(cart.cart_level_discount_applications || []).length;
    }

    $$('[data-cente-cart-empty]').forEach(function (n) { n.hidden = count > 0; });
    $$('[data-cente-cart-body]').forEach(function (n) { n.hidden = count === 0; });
  }

  function renderReward(root) {
    var rem = remaining();
    var limit = threshold();
    var pct = limit > 0 ? Math.min(1, Math.max(0, eligibleSubtotal() / limit)) : 0;
    var slots = promisedSlots().length;
    var noun = slots === 1 ? 'item' : 'items';
    var unlocked = rem === 0 && slots > 0;

    var bar = $('[data-cente-reward]', root);
    if (bar) {
      bar.hidden = !CFG.enabled || !(cart.items || []).length;
      if (unlocked) bar.setAttribute('data-unlocked', '');
      else bar.removeAttribute('data-unlocked');
    }

    var msg = $('[data-cente-reward-message]', root);
    if (msg) {
      msg.textContent = unlocked
        ? 'FREE GIFTS UNLOCKED! CHECKOUT TO CLAIM'
        : money(rem) + ' away from ' + slots + ' FREE ' + noun;
    }

    var fill = $('[data-cente-reward-fill]', root);
    if (fill) fill.style.width = (pct * 100).toFixed(2) + '%';

    var track = $('[data-cente-reward-track]', root);
    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(pct * 100)));
      track.setAttribute('aria-valuetext', unlocked ? 'Offer unlocked' : money(rem) + ' remaining');
    }

    // Express checkout is hidden only while the offer is unlocked and the shopper
    // has not confirmed sizes — those buttons cannot be intercepted, so leaving
    // them would let an unconfirmed size ship and skip the upsell.
    var express = $('[data-cente-express]', root);
    if (express) express.hidden = unlocked && !sizesConfirmed;
    var expressNote = $('[data-cente-express-note]', root);
    if (expressNote) expressNote.hidden = !(unlocked && !sizesConfirmed);

    // Upsell modal carries its own live copy of the readout.
    var upMsg = $('[data-cente-upsell-message]');
    if (upMsg) {
      upMsg.textContent = unlocked
        ? 'Your gifts are unlocked and in your cart.'
        : 'Add ' + money(rem) + " more and we'll include " + slots + ' free ' + noun + '.';
    }
    var upFill = $('[data-cente-upsell-fill]');
    if (upFill) upFill.style.width = (pct * 100).toFixed(2) + '%';
    var upCta = $('[data-cente-upsell-proceed] span');
    if (upCta) upCta.textContent = unlocked ? 'Continue to checkout' : 'Checkout without gifts';
  }

  function render() {
    try {
      if (!cart) return;
      $$('[data-cente-cart]').forEach(function (root) {
        // Each surface is isolated: a throw while painting the drawer must not
        // leave the cart page unpainted, or vice versa.
        try {
          renderLines(root);
          renderTotals(root);
          renderReward(root);
        } catch (err) {
          console.error('[cente-cart] render failed for a surface', err);
        }
      });
    } finally {
      // ALWAYS clear busy. It used to be cleared on the last line of this function,
      // so any render error left busy stuck true — which silently dead-ended the
      // checkout button and the quantity controls.
      setBusy(false);
    }
  }

  function setBusy(state) {
    busy = state;
    $$('[data-cente-cart]').forEach(function (root) {
      root.toggleAttribute('data-busy', state);
    });
  }

  /**
   * Refresh from the server, reconcile gifts, then paint once.
   * Reconciling BEFORE painting avoids showing a state that is about to change.
   */
  function sync() {
    return queue(function () {
      setBusy(true);
      return getCart()
        .then(function (fresh) { cart = fresh; return reconcile(); })
        .then(function () { return getCart(); })
        .then(function (fresh) { cart = fresh; })
        .catch(function (err) { console.error('[cente-cart]', err); })
        .then(render, render);
    });
  }

  /* ------------------------------------------------------------------ *
   * mutations
   * ------------------------------------------------------------------ */

  /**
   * Apply a quantity change to the LOCAL cart copy so the UI can repaint on the
   * same tick the shopper clicks. Line prices are scaled from the existing unit
   * price, which is exact for undiscounted lines and momentarily approximate for
   * discounted ones — the server response corrects it a moment later.
   */
  function applyLocalQuantity(key, quantity) {
    if (!cart || !cart.items) return;
    var kept = [];
    cart.items.forEach(function (l) {
      if (l.key !== key) { kept.push(l); return; }
      if (quantity <= 0) return;                       // dropped
      var unitFinal = (Number(l.final_line_price) || 0) / Math.max(1, Number(l.quantity) || 1);
      var unitOrig = (Number(l.original_line_price) || 0) / Math.max(1, Number(l.quantity) || 1);
      l.quantity = quantity;
      l.final_line_price = Math.round(unitFinal * quantity);
      l.original_line_price = Math.round(unitOrig * quantity);
      kept.push(l);
    });
    cart.items = kept;
    cart.item_count = kept.reduce(function (n, l) { return n + (Number(l.quantity) || 0); }, 0);
    cart.items_subtotal_price = kept.reduce(function (n, l) {
      return n + (Number(l.original_line_price) || 0);
    }, 0);
    cart.total_price = kept.reduce(function (n, l) {
      return n + (Number(l.final_line_price) || 0);
    }, 0);
  }

  /**
   * Coalesce rapid clicks on the same line into ONE request.
   *
   * Three taps on "+" used to mean three full round trips, each blocking the UI.
   * Now the local copy updates immediately, the row repaints, and a single write
   * goes out once the shopper stops tapping.
   */
  var pendingQty = {};
  var qtyTimer = null;

  function nudgeLine(key, quantity) {
    pendingQty[key] = quantity;
    applyLocalQuantity(key, quantity);
    render();                                          // instant feedback

    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(function () {
      var batch = pendingQty;
      pendingQty = {};
      Object.keys(batch).forEach(function (k) { changeLine(k, batch[k]); });
    }, 220);
  }

  function changeLine(key, quantity) {
    return queue(function () {
      setBusy(true);
      // B-011: keyed on line.key, never an index.
      return post(route('cart_change_url', '/cart/change') + '.js', { id: key, quantity: quantity })
        // The change.js response IS the updated cart, with valid line keys, so
        // trust it and paint straight away instead of spending a round trip
        // re-reading. reconcile() below still re-reads before it mutates anything.
        .then(function (updated) {
          if (updated && updated.items) { cart = updated; render(); }
          return reconcile();
        })
        // Only re-read when reconcile actually changed something; it returns false
        // when there was nothing to do, which is the common case.
        .then(function (changed) { if (changed) return getCart().then(function (f) { cart = f; }); })
        .catch(function (err) {
          console.error('[cente-cart]', err);
          return getCart().then(function (f) { cart = f; });
        })
        .then(render, render);
    });
  }

  function addVariant(id, quantity) {
    return queue(function () {
      setBusy(true);
      return post(route('cart_add_url', '/cart/add') + '.js', {
        items: [{ id: Number(id), quantity: quantity || 1 }]
      })
        .then(getCart)
        .then(function (fresh) { cart = fresh; return reconcile(); })
        .then(function () { return getCart(); })
        .then(function (fresh) { cart = fresh; render(); })
        .catch(function (err) {
          console.error('[cente-cart]', err);
          return getCart().then(function (f) { cart = f; render(); }).then(function () { throw err; });
        });
    });
  }

  /* ------------------------------------------------------------------ *
   * drawer
   * ------------------------------------------------------------------ */

  function openDrawer() {
    var drawer = $('[data-cente-drawer]');
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('data-open', '');
    document.documentElement.style.overflow = 'hidden';
    var close = $('[data-cente-drawer-close]', drawer);
    if (close) close.focus();
    sync();
  }

  function closeDrawer() {
    var drawer = $('[data-cente-drawer]');
    if (!drawer) return;
    drawer.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /**
   * Open a dialog, trying every avenue before admitting defeat.
   *
   * showModal() can throw (already-open dialogs, detached nodes, older engines).
   * When it did, this used to return false and the caller sent the shopper to
   * checkout — which is why the popups appeared to be skipped entirely. Now it
   * falls back to a forced non-modal open with inline positioning, so the dialog is
   * visible even if the top layer is unavailable.
   *
   * @returns {boolean} whether the dialog is now on screen.
   */
  function openModal(attr) {
    var node = $('[' + attr + ']');
    if (!node) {
      console.error('[cente-cart] dialog not in DOM:', attr);
      return false;
    }

    if (node.open) return true;

    try {
      if (typeof node.showModal === 'function') {
        node.showModal();
        if (node.open) return true;
      }
    } catch (err) {
      console.warn('[cente-cart] showModal failed, forcing open', attr, err);
    }

    // Forced fallback: plain `open` plus inline centring, because a non-modal
    // dialog is absolutely positioned in normal flow and can land off-screen.
    try {
      node.setAttribute('open', '');
      node.style.position = 'fixed';
      node.style.top = '50%';
      node.style.left = '50%';
      node.style.transform = 'translate(-50%, -50%)';
      node.style.zIndex = '2147483647';
      node.style.display = 'block';
      return true;
    } catch (err) {
      console.error('[cente-cart] could not open', attr, err);
      return false;
    }
  }

  function closeModal(attr) {
    var node = $('[' + attr + ']');
    if (node && node.open && typeof node.close === 'function') node.close();
  }

  function toCheckout() {
    window.location.href = route('cart_url', '/cart').replace(/\/cart$/, '') + '/checkout';
  }

  /* ------------------------------------------------------------------ *
   * gift size modal
   * ------------------------------------------------------------------ */

  function seedGiftSelects() {
    var bySlot = {};
    giftLines().forEach(function (l) {
      var s = slotOf(l);
      if (s) bySlot[s] = l.variant_id;
    });
    SLOTS.forEach(function (s) {
      var entry = CFG.gifts[s.key];
      var row = $('[data-cente-gift-slot="' + s.slot + '"]');
      if (row) row.hidden = !stocked(entry);
      if (!entry || !bySlot[s.slot]) return;
      entry.variants.forEach(function (v) {
        if (String(v.id) !== String(bySlot[s.slot])) return;
        var sizeSel = $('[data-cente-gift-size="' + s.slot + '"]');
        if (sizeSel && !sizeSel.value && v.size) sizeSel.value = v.size;
        var colSel = $('[data-cente-gift-colour="' + s.slot + '"]');
        if (colSel && !colSel.value && v.color) colSel.value = v.color;
      });
    });
  }

  function confirmGiftSizes(button) {
    var chosen = [];
    var incomplete = false;

    promisedSlots().forEach(function (s) {
      var sizeSel = $('[data-cente-gift-size="' + s.slot + '"]');
      var colSel = $('[data-cente-gift-colour="' + s.slot + '"]');
      var size = sizeSel ? sizeSel.value : '';
      var colour = colSel ? colSel.value : '';
      if (!size || (colSel && !colour)) { incomplete = true; return; }
      var v = pickVariant(CFG.gifts[s.key], { size: size, colour: colSel ? colour : null });
      if (!v) { incomplete = true; return; }
      chosen.push({ slot: s.slot, id: v.id });
    });

    var err = $('[data-cente-gift-error]');
    if (incomplete) {
      if (err) { err.textContent = 'Choose a size for each gift to continue.'; err.hidden = false; }
      return;
    }
    if (err) err.hidden = true;
    button.setAttribute('aria-busy', 'true');

    queue(function () {
      try {
        return applyGiftChoices(button, chosen, err);
      } catch (e) {
        // Anything thrown while BUILDING the work (not running it) used to be
        // swallowed by queue()'s catch, leaving the button stuck in its busy state
        // with no message. Surface it instead.
        console.error('[cente-cart] confirm failed', e);
        button.removeAttribute('aria-busy');
        if (err) {
          err.textContent = 'Something went wrong. Please try again.';
          err.hidden = false;
        }
      }
    });
  }

  /**
   * Apply the chosen gift variants, then continue to checkout.
   *
   * Re-reads /cart.js FIRST. Line keys are only valid against the cart they came
   * from, and acting on a stale local copy is exactly what broke gift removal
   * earlier — /cart/change.js rejects a key it cannot find, which surfaced as a
   * generic "something went wrong".
   */
  function applyGiftChoices(button, chosen, err) {
    return getCart().then(function (fresh) {
      cart = fresh;
      var current = giftLines();
      var toRemove = [];
      var toAdd = [];
      var badKeys = [];

      chosen.forEach(function (c) {
        var existing = null;
        current.forEach(function (l) { if (slotOf(l) === c.slot) existing = l; });
        if (!existing) { toAdd.push(c); return; }
        if (String(existing.variant_id) === String(c.id)) return;   // already correct

        // B-011: mutations are keyed on line.key. A missing/blank key is the one
        // thing that must never be sent — JSON.stringify DROPS an undefined value,
        // so the request body arrives as {"quantity":0} and Shopify answers
        // "no valid id or line parameter".
        if (typeof existing.key === 'string' && existing.key) {
          toRemove.push(existing.key);
        } else {
          badKeys.push({ slot: c.slot, variant_id: existing.variant_id, key: existing.key });
        }
        toAdd.push(c);
      });

      if (badKeys.length) {
        console.error('[cente-cart] gift lines with no usable key — cannot remove', badKeys, current);
      }

      var work = Promise.resolve();

      // ADD FIRST, then remove. If the add fails we have not already destroyed the
      // shopper's existing gift line, and the new choice is never blocked by a
      // problem removing the old one.
      if (toAdd.length) {
        work = work.then(function () {
          return post(route('cart_add_url', '/cart/add') + '.js', {
            items: toAdd.map(function (c) {
              return {
                id: Number(c.id),
                quantity: 1,
                properties: { _gift: GIFT_FLAG, _gift_slot: c.slot }
              };
            })
          });
        });
      }

      // Superseded lines are removed on a best-effort basis: a failure here leaves
      // a duplicate slot, which the next reconcile() cleans up, rather than
      // blocking checkout.
      toRemove.forEach(function (key) {
        work = work.then(function () {
          return post(route('cart_change_url', '/cart/change') + '.js', { id: key, quantity: 0 })
            .catch(function (e) {
              console.warn('[cente-cart] could not remove superseded gift line', key, e && e.message);
            });
        });
      });
      return work
        // VERIFY before leaving. Adding before removing can leave a superseded
        // line behind if the targeted removal failed, and a 4th gift line is not
        // covered by the discount's "customer gets 3" — the shopper would be
        // charged for it. So re-read with fresh keys and delete anything that is
        // not exactly what was chosen.
        .then(function () { return getCart(); })
        .then(function (fresh) {
          cart = fresh;
          var want = {};
          chosen.forEach(function (c) { want[c.slot] = String(c.id); });

          var keep = {};
          var extras = [];
          giftLines().forEach(function (l) {
            var slot = slotOf(l);
            if (!slot || !want[slot]) return;                 // reconcile handles these
            if (String(l.variant_id) !== want[slot]) { extras.push(l); return; }
            if (keep[slot]) { extras.push(l); return; }        // same variant twice
            keep[slot] = true;
          });

          if (!extras.length) return null;
          console.warn('[cente-cart] clearing superseded gift lines', extras.map(function (l) {
            return { key: l.key, slot: slotOf(l), variant_id: l.variant_id };
          }));

          var cleanup = Promise.resolve();
          extras.forEach(function (l) {
            if (typeof l.key !== 'string' || !l.key) {
              console.error('[cente-cart] superseded line has no key, cannot remove', l);
              return;
            }
            cleanup = cleanup.then(function () {
              return post(route('cart_change_url', '/cart/change') + '.js', { id: l.key, quantity: 0 })
                .catch(function (e) {
                  console.error('[cente-cart] cleanup removal failed', l.key, e && e.message, e && e.payload);
                });
            });
          });
          return cleanup;
        })
        .then(function () { sizesConfirmed = true; toCheckout(); })
        .catch(function (e) {
          console.error('[cente-cart] gift write failed', {
            message: e && e.message,
            status: e && e.status,
            payload: e && e.payload,
            request: e && e.request,
            chosen: chosen,
            cartGifts: giftLines().map(function (l) {
              return { key: l.key, variant_id: l.variant_id, slot: slotOf(l), qty: l.quantity };
            })
          });
          button.removeAttribute('aria-busy');
          if (err) {
            // Shopify's own wording is far more useful than a generic apology —
            // "sold out", "you can only add 1", a stale line key, and so on.
            err.textContent = (e && e.message)
              ? ('Could not add your gifts: ' + e.message)
              : 'Something went wrong adding your gifts. Please try again.';
            err.hidden = false;
          }
          // Re-read so the cart on screen reflects whatever actually happened.
          return getCart().then(function (f) { cart = f; render(); }).catch(function () {});
        });
    });
  }

  /* ------------------------------------------------------------------ *
   * events — all delegated at document level, so re-rendered nodes keep working
   * ------------------------------------------------------------------ */

  function onClick(event) {
    var t = event.target;
    if (!(t instanceof Element)) return;

    if (t.closest('[data-cente-drawer-open]')) { event.preventDefault(); return openDrawer(); }
    if (t.closest('[data-cente-drawer-close]') || t.closest('[data-cente-drawer-scrim]')) {
      event.preventDefault(); return closeDrawer();
    }

    var inc = t.closest('[data-cente-line-inc]');
    var dec = t.closest('[data-cente-line-dec]');
    var rm = t.closest('[data-cente-line-remove]');
    if (inc || dec || rm) {
      event.preventDefault();
      // Deliberately NOT gated on `busy`. Updates are optimistic and coalesced, so
      // the controls stay usable while a write is in flight — that responsiveness
      // is the point.
      var row = (inc || dec || rm).closest('[data-key]');
      if (!row) return;
      var key = row.getAttribute('data-key');
      var line = (cart.items || []).filter(function (l) { return l.key === key; })[0];
      if (!line) return;
      if (rm) return nudgeLine(key, 0);
      var next = Number(line.quantity) + (inc ? 1 : -1);
      if (next < 0) next = 0;
      return nudgeLine(key, next);
    }

    // Checkout gate — the only path to checkout.
    //
    // Deliberately NOT gated on `busy`, and every failure falls through to
    // checkout. A dead checkout button is the single worst outcome here, so if the
    // gate cannot run for any reason the shopper still gets to pay.
    if (t.closest('[data-cente-checkout]')) {
      event.preventDefault();
      try {
        if (!CFG || !CFG.enabled || !cart) return toCheckout();

        // NOTE: deliberately NOT gated on promisedSlots(). If the gift products fail
        // to resolve (e.g. a tee is not storefront-visible) the offer must fail
        // VISIBLY, not silently send the shopper to checkout as though no offer
        // existed. Below the threshold they still deserve to be told.
        if (remaining() > 0) {
          // Below the threshold — ask before letting them walk away from the gifts.
          if (openModal('data-cente-upsell')) return;
          // Last resort: if the dialog genuinely cannot be shown, still ASK rather
          // than silently continuing. Being sent to checkout unasked is the one
          // outcome this branch exists to prevent.
          var slots = promisedSlots().length || 3;
          var msg = 'You are ' + money(remaining()) + ' away from ' + slots +
            ' free gifts. Continue to checkout without them?';
          if (!window.confirm(msg)) return;
          return toCheckout();
        }

        if (!sizesConfirmed || !giftsComplete()) {
          // Qualifying, and the sizes in the cart are still our defaults — the
          // shopper must confirm colour/size before this can go through.
          //
          // seedGiftSelects() is isolated: a throw in there used to bubble to the
          // gate's catch, which sent the shopper to checkout instead of asking.
          try {
            seedGiftSelects();
          } catch (seedErr) {
            console.error('[cente-cart] seedGiftSelects failed', seedErr);
          }

          if (openModal('data-cente-gift-modal')) return;

          // Dialog genuinely unavailable. Never ship default sizes silently.
          console.error('[cente-cart] gift size dialog unavailable', {
            promised: promisedSlots().map(function (x) { return x.slot; }),
            gifts: CFG.gifts
          });
          if (!window.confirm(
            'We could not open the gift size picker. Continue to checkout and we will ' +
            'contact you about your free gift sizes?'
          )) return;
          return toCheckout();
        }

        return toCheckout();
      } catch (err) {
        console.error('[cente-cart] checkout gate failed, proceeding to checkout', err);
        return toCheckout();
      }
    }

    var confirmBtn = t.closest('[data-cente-gift-confirm]');
    if (confirmBtn) { event.preventDefault(); return confirmGiftSizes(confirmBtn); }

    if (t.closest('[data-cente-upsell-proceed]')) {
      event.preventDefault();
      // Deliberately does NOT set sizesConfirmed. Skipping gifts below the
      // threshold is not the same as having chosen gift sizes; conflating them made
      // the size modal think it had already been answered.
      closeModal('data-cente-upsell');
      return toCheckout();
    }

    var addBtn = t.closest('[data-cente-upsell-add]');
    if (addBtn) {
      event.preventDefault();
      var card = addBtn.closest('[data-cente-upsell-card]');
      var sel = card && $('[data-cente-upsell-variant]', card);
      if (!sel || !sel.value) return;
      addBtn.setAttribute('aria-busy', 'true');
      var uErr = $('[data-cente-upsell-error]');
      if (uErr) uErr.hidden = true;
      addVariant(sel.value, 1)
        .then(function () { addBtn.removeAttribute('aria-busy'); })
        .catch(function () {
          addBtn.removeAttribute('aria-busy');
          if (uErr) { uErr.textContent = 'Could not add that. Please try again.'; uErr.hidden = false; }
        });
    }
  }

  function onKeydown(event) {
    if (event.key !== 'Escape') return;
    var drawer = $('[data-cente-drawer]');
    if (drawer && drawer.hasAttribute('data-open')) closeDrawer();
  }

  function onChange(event) {
    var t = event.target;
    if (!(t instanceof Element)) return;
    if (t.matches('[data-cente-gift-size], [data-cente-gift-colour]')) {
      var err = $('[data-cente-gift-error]');
      if (err) err.hidden = true;
    }
  }

  /**
   * Adding from a PDP opens the cart.
   *
   * Horizon's CartAddEvent has NO `static eventName` (only CartUpdateEvent does),
   * so `super(CartAddEvent.eventName, ...)` dispatches an event whose type is the
   * literal string "undefined". Horizon's own drawer listened with the same
   * undefined constant, so it worked by accident. We listen for both the sane name
   * and the actual one, so this keeps working whether or not Shopify fixes it.
   */
  function onCartAdd() {
    openDrawer();   // opens immediately and syncs itself — no wait for the fetch
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  function boot() {
    var cfgNode = $('[data-cente-cart-config]');
    if (!cfgNode) return;
    try {
      CFG = JSON.parse(cfgNode.textContent || '{}');
    } catch (e) {
      console.error('[cente-cart] bad config', e);
      return;
    }
    CFG.gifts = CFG.gifts || {};
    CFG.eligible = {};
    (String(CFG.eligibleRaw || '').match(/\d{5,}/g) || []).forEach(function (id) { CFG.eligible[id] = true; });

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('change', onChange);
    document.addEventListener('cart:add', onCartAdd);
    document.addEventListener(String(undefined), onCartAdd);   // see onCartAdd
    // Anything else on the page that mutates the cart (PDP forms, apps) should
    // still be reflected here. This renderer never dispatches cart:update itself,
    // so there is no loop to guard against.
    document.addEventListener('cart:update', function () { sync(); });
    document.addEventListener('cente:cart:refresh', function () { sync(); });

    var seed = $('[data-cente-cart-seed]');
    if (seed) {
      try { cart = JSON.parse(seed.textContent || 'null'); } catch (e) { cart = null; }
    }
    if (cart) { render(); sync(); } else { sync(); }

    window.__centeCart = {
      state: function () {
        return {
          items: (cart && cart.items && cart.items.length) || 0,
          gifts: cart ? giftLines().length : 0,
          currency: cart && cart.currency,
          rate: (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || null,
          eligibleSubtotal: cart ? eligibleSubtotal() : null,
          thresholdBase: CFG.threshold,
          thresholdConverted: threshold(),
          remaining: cart ? remaining() : null,
          qualifies: cart ? qualifies() : null,
          promisedSlots: promisedSlots().map(function (s) { return s.slot; }),
          giftsComplete: cart ? giftsComplete() : null,
          sizesConfirmed: sizesConfirmed,
          giftValue: giftValue(),
          // Every line, so a surviving Terrain can be identified: a real gift line
          // carries _gift/_gift_slot; a PAID Terrain zeroed by the BXGY does not,
          // and this renderer must not touch it.
          lines: ((cart && cart.items) || []).map(function (l) {
            return {
              key: l.key,
              product_id: l.product_id,
              title: l.product_title || l.title,
              qty: l.quantity,
              price: l.final_line_price,
              isGift: R().isGift(l),
              slot: R().slotOf(l),
              properties: l.properties
            };
          }),
          toRemove: cart ? R().giftsToRemove(cart, CFG, rate()) : null,
          toAdd: cart ? R().giftsToAdd(cart, CFG, rate()) : null
        };
      },
      sync: sync,
      open: openDrawer,
      /** What the checkout button would do right now, and why. */
      checkoutPlan: function () {
        if (!CFG || !CFG.enabled) return 'straight to checkout — offer disabled';
        if (!cart) return 'straight to checkout — no cart loaded';
        var slots = promisedSlots().map(function (x) { return x.slot; });
        var resolved = ['terrain', 'tee1', 'tee2'].map(function (k) {
          var e = (CFG.gifts || {})[k];
          return k + '=' + (!e ? 'NULL (not storefront-visible?)' : (stocked(e) ? 'ok' : 'no stock'));
        }).join(', ');
        if (remaining() > 0) {
          return 'upsell modal (' + (document.querySelector('[data-cente-upsell]') ? 'found' : 'MISSING') +
            ') remaining=' + remaining() + ' [' + resolved + ']';
        }
        if (!sizesConfirmed || !giftsComplete()) {
          return 'gift size modal (' + (document.querySelector('[data-cente-gift-modal]') ? 'found' : 'MISSING') +
            ') slots=' + slots.join('/') + ' [' + resolved + ']';
        }
        if (!slots.length) return 'straight to checkout — no gifts resolved [' + resolved + ']';
        return 'straight to checkout — sizes already confirmed [' + resolved + ']';
      },
      busy: function () { return busy; }
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
